import React, { useState, useRef, useEffect } from 'react';
import './CategorySelect.css';

interface Category {
    id: string;
    name: string;
}

interface CategorySelectProps {
    categories: Category[];
    value: string;
    onChange: (categoryId: string) => void;
    onCreateCategory: (name: string) => Promise<Category | null>;
}

export const CategorySelect: React.FC<CategorySelectProps> = ({
    categories,
    value,
    onChange,
    onCreateCategory
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedCategory = categories.find(cat => cat.id === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchInput('');
                setIsCreating(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredCategories = categories.filter(category =>
        category.name.toLowerCase().includes(searchInput.toLowerCase())
    );

    const handleSelect = (categoryId: string) => {
        onChange(categoryId);
        setIsOpen(false);
        setSearchInput('');
    };

    const handleCreate = async () => {
        if (searchInput.trim() && !isCreating) {
            setIsCreating(true);
            try {
                const newCategory = await onCreateCategory(searchInput);
                if (newCategory) {
                    onChange(newCategory.id);
                    setIsOpen(false);
                    setSearchInput('');
                }
            } finally {
                setIsCreating(false);
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Prevent form submission
            if (filteredCategories.length > 0) {
                handleSelect(filteredCategories[0].id);
            } else if (searchInput.trim()) {
                handleCreate();
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchInput('');
            setIsCreating(false);
        }
    };

    return (
        <div className="category-select-container" ref={dropdownRef}>
            <div
                className="category-select-trigger"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span>{selectedCategory?.name || 'Select category'}</span>
            </div>
            {isOpen && (
                <div className="category-select-dropdown">
                    <input
                        type="text"
                        className="category-select-search"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Search categories..."
                        autoFocus
                    />
                    <div className="category-select-options">
                        {filteredCategories.map(category => (
                            <div
                                key={category.id}
                                className="category-select-option"
                                onClick={() => handleSelect(category.id)}
                            >
                                {category.name}
                            </div>
                        ))}
                        {searchInput && filteredCategories.length === 0 && !isCreating && (
                            <div
                                className="category-select-create"
                                onClick={handleCreate}
                            >
                                {isCreating ? 'Creating...' : `Create "${searchInput}"`}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}; 